import React, { useCallback, useEffect, useState } from "react";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, RefreshCcw, KeyRound, AlertTriangle, Copy, ArrowLeft } from "lucide-react";

import awsConfig from "../aws-config";
import { resolveAuthToken } from "../utils/authToken";
import { usePageMetadata } from "../hooks/usePageMetadata";
import "./IntegrationsPage.css";

const INTEGRATIONS_API_URL = awsConfig.api_url_integrations_key;
const MENU_API_URL = awsConfig.api_url_get_menu;
const SECURE_MENU_BASE = awsConfig.api_url_secure_menu;

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatShortcode = (storeId, plaintextKey) =>
  `[brandmykitchen_menu id="${storeId}" key="${plaintextKey}"]`;

const formatRelativeTime = (iso) => {
  if (!iso) return null;
  try {
    const target = new Date(iso);
    if (Number.isNaN(target.getTime())) return null;
    const diffMs = Date.now() - target.getTime();
    if (diffMs < 0) return "just now";
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    }
    return target.toLocaleDateString();
  } catch {
    return null;
  }
};

const PLAN_CARD_META = {
  free: {
    name: "Free",
    accent: "var(--sys-primary-soft)",
    accentSoft: "var(--sys-chip-strong-bg)",
    description: "Secure embeds for one live menu. Includes 500 secure requests per month.",
  },
  starter: {
    name: "Starter",
    accent: "var(--sys-primary)",
    accentSoft: "var(--sys-chip-strong-bg)",
    description: "Built for multi-unit operators. Up to 3 stores and 5,000 secure requests per month.",
  },
  enterprise: {
    name: "Enterprise",
    accent: "var(--sys-text)",
    accentSoft: "var(--sys-surface-contrast)",
    description: "High-volume allowance with priority support and 20,000 secure requests per month.",
  },
  lifetime_studio: {
    name: "Lifetime · Studio 10",
    accent: "var(--color-warning)",
    accentSoft: "var(--sys-chip-muted-bg)",
    description: "Black Friday LTD: 10 stores, ~5,000 monthly orders and 30,000 secure requests.",
  },
  lifetime_agency: {
    name: "Lifetime · Agency 25",
    accent: "var(--sys-primary)",
    accentSoft: "var(--sys-chip-strong-bg)",
    description: "Black Friday LTD: 25 stores, ~25,000 monthly orders and 75,000 secure requests.",
  },
  lifetime_enterprise: {
    name: "Lifetime · Enterprise 50",
    accent: "var(--sys-accent-green)",
    accentSoft: "var(--sys-chip-muted-bg)",
    description: "Black Friday LTD: 50 stores, ~100,000 monthly orders and 120,000 secure requests.",
  },
  default: {
    name: "Brandmykitchen",
    accent: "var(--sys-text)",
    accentSoft: "var(--sys-surface-contrast)",
    description: "Custom allowances agreed with Brandmykitchen.",
  },
};

const formatNumber = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  return value.toLocaleString("en-GB");
};

const formatLimitValue = (used, limit) => {
  if (typeof limit === "number") {
    return `${formatNumber(used)} / ${formatNumber(limit)}`;
  }
  return `${formatNumber(used)}`;
};

const resolvePlanMeta = (plan) => {
  const key = (plan?.plan || "default").toLowerCase();
  return PLAN_CARD_META[key] || PLAN_CARD_META.default;
};

const resolveGenerationLock = (plan, business) => {
  if (business.canGenerateMore !== false) {
    return null;
  }
  const keysLimit = business.keysLimit ?? plan?.maxKeys ?? null;
  const storesLimit = business.storesLimit ?? plan?.maxStores ?? null;
  const keysUsed = business.keysUsed ?? 0;
  const storesUsed = business.storesUsed ?? 0;

  if (typeof keysLimit === "number" && keysUsed >= keysLimit) {
    return `You've used all ${keysLimit} secure keys for this plan. Revoke one or upgrade to create another.`;
  }
  if (typeof storesLimit === "number" && storesUsed >= storesLimit) {
    return `Plan allows ${storesLimit} stores. Archive a store or upgrade to connect another menu.`;
  }
  return "Plan limit reached. Revoke a key or upgrade your plan to continue.";
};

const usageStatusForKey = (key = {}, plan = {}) => {
  const lifetimeUsage = key?.usageCount ?? 0;
  const monthlyUsage = key?.monthlyRequestCount ?? lifetimeUsage;
  const threshold = plan?.usageAlertThreshold ?? null;
  const cap = plan?.secureRequestsPerMonth ?? null;

  if (cap && monthlyUsage >= cap) {
    return {
      level: "critical",
      label: "Monthly cap reached",
      description:
        "This key has consumed the full monthly allowance. Revoke unused embeds or upgrade your plan.",
    };
  }
  if (threshold && monthlyUsage >= threshold) {
    return {
      level: "warning",
      label: "High activity",
      description:
        "Usage is close to the monthly allowance. Confirm the embed is configured as expected.",
    };
  }
  if (!threshold && !cap && lifetimeUsage > 0 && monthlyUsage > 0) {
    return null;
  }
  return null;
};

export default function IntegrationsPage() {
  usePageMetadata({
    title: "Integrations · Brandmykitchen",
    description:
      "Generate API keys and shortcodes to embed your Brandmykitchen menu on WordPress and other platforms.",
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [plainKeys, setPlainKeys] = useState({});
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState("");

  const integrationsUrlAvailable = Boolean(INTEGRATIONS_API_URL);

  const getAuthToken = useCallback(async () => {
    const session = await fetchAuthSession();
    const token = resolveAuthToken(session);
    if (!token) throw new Error("Authentication required");
    return token;
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!integrationsUrlAvailable) {
        setError(
          "The integrations API endpoint has not been configured. Confirm the frontend environment variables."
        );
        setBusinesses([]);
        if (!silent) {
          setLoading(false);
        }
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      setError("");

      try {
        const user = await getCurrentUser().catch(() => null);
        if (!user) {
          navigate("/auth");
          return;
        }

        const token = await getAuthToken();
        const headers = { Authorization: `Bearer ${token}` };

        const integrationsResponse = await fetch(INTEGRATIONS_API_URL, {
          headers,
        });
        if (!integrationsResponse.ok) {
          throw new Error(
            (await safeJsonMessage(integrationsResponse)) ||
              "Failed to load integration keys."
          );
        }

        const payload = await integrationsResponse.json();
        const list = Array.isArray(payload?.businesses)
          ? payload.businesses
          : [];

        const seenKeyIds = new Set();
        const enriched = await Promise.all(
          list.map(async (biz) => {
            const businessId = biz.businessId;
            let stores = [];
            const keys = Array.isArray(biz.keys) ? biz.keys : [];
            keys.forEach((key) => {
              if (key?.keyId) {
                seenKeyIds.add(key.keyId);
              }
            });
            if (MENU_API_URL && businessId) {
              try {
                const menuResponse = await fetch(
                  `${MENU_API_URL}/${encodeURIComponent(businessId)}`,
                  { headers }
                );
                if (menuResponse.ok) {
                  const menuData = await menuResponse.json();
                  stores = menuData?.publishes?.active ?? [];
                }
              } catch (err) {
                console.warn("Failed to load stores for business", err);
              }
            }

            return {
              ...biz,
              stores,
            };
          })
        );

        setBusinesses(enriched);
        setPlainKeys((prev) => {
          if (seenKeyIds.size === 0) {
            return Object.keys(prev).length ? {} : prev;
          }
          const next = {};
          seenKeyIds.forEach((id) => {
            if (prev[id]) {
              next[id] = prev[id];
            }
          });
          return Object.keys(next).length === Object.keys(prev).length
            ? prev
            : next;
        });
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to fetch integration data.");
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [getAuthToken, integrationsUrlAvailable, navigate]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setFlash(null);
    try {
      await loadData({ silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerate = async (businessId, storeId) => {
    if (!INTEGRATIONS_API_URL) return;
    setRefreshing(true);
    setFlash(null);
    try {
      const token = await getAuthToken();
      const response = await fetch(INTEGRATIONS_API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ businessId, storeId }),
      });

      if (!response.ok) {
        throw new Error(
          (await safeJsonMessage(response)) ||
            `Failed to generate a key (status ${response.status}).`
        );
      }

      const data = await response.json();
      const { key: plaintextKey, keyId, storeId: responseStoreId } = data || {};
      if (plaintextKey && keyId) {
        const resolvedStoreId = responseStoreId || storeId;
        setPlainKeys((prev) => ({
          ...prev,
          [keyId]: {
            key: plaintextKey,
            storeId: resolvedStoreId,
          },
        }));
      }
      setFlash({
        type: "success",
        message: "Key generated. Copy the shortcode now—this is the only time the key will be visible.",
      });
      await loadData({ silent: true });
    } catch (err) {
      console.error(err);
      setFlash({
        type: "error",
        message: err.message || "Unable to generate a new key.",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleRevoke = async (businessId, keyId) => {
    if (!INTEGRATIONS_API_URL) return;
    setRefreshing(true);
    setFlash(null);
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${INTEGRATIONS_API_URL}/${encodeURIComponent(
          keyId
        )}?businessId=${encodeURIComponent(businessId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          (await safeJsonMessage(response)) ||
            `Failed to revoke key (status ${response.status}).`
        );
      }

      setFlash({
        type: "success",
        message: "Key revoked. Existing embeds using this key will stop working immediately.",
      });
      setPlainKeys((prev) => {
        if (!prev[keyId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[keyId];
        return next;
      });
      await loadData({ silent: true });
    } catch (err) {
      console.error(err);
      setFlash({
        type: "error",
        message: err.message || "Unable to revoke the key.",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlash({ type: "success", message });
    } catch (err) {
      console.error("Clipboard error", err);
      setFlash({
        type: "error",
        message:
          "Unable to copy to the clipboard. Please copy the shortcode manually.",
      });
    }
  };

  const headerDescription = SECURE_MENU_BASE
    ? `Use this base endpoint to embed your Brandmykitchen asset: ${SECURE_MENU_BASE.replace(
        /\/$/,
        ""
      )}`
    : "Use these keys to embed your Brandmykitchen asset in WordPress and other CMS platforms.";
  return (
    <div className="integrations-page">
      <div className="integrations-breadcrumb">
        <Link to="/integrations" className="integrations-back-link">
          <ArrowLeft size={16} />
          <span>All integrations</span>
        </Link>
      </div>
      <div className="integrations-header">
        <div>
          <h1>
            <ShieldCheck size={24} />
            <span>Integrations</span>
          </h1>
          <p>{headerDescription}</p>
        </div>
        <button
          className="btn btn-outline integrations-refresh"
          onClick={handleRefresh}
          disabled={refreshing || loading}
        >
          <RefreshCcw size={16} />
          Refresh
        </button>
      </div>

      {flash && (
        <div
          className={`integrations-flash integrations-flash-${flash.type}`}
          role="status"
        >
          {flash.type === "error" ? (
            <AlertTriangle size={18} />
          ) : (
            <ShieldCheck size={18} />
          )}
          <span>{flash.message}</span>
          <button
            className="integrations-flash-dismiss"
            onClick={() => setFlash(null)}
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}

      {!integrationsUrlAvailable && (
        <div className="integrations-warning">
          <AlertTriangle size={18} />
          <div>
            <strong>Missing endpoint configuration.</strong>
            <p>
              Set <code>REACT_APP_API_URL_INTEGRATIONS_KEY</code> and redeploy
              the dashboard to enable API key management.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="integrations-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="integrations-empty">Loading integrations…</div>
      ) : businesses.length === 0 ? (
        <div className="integrations-empty">
          No businesses found for this account.
        </div>
      ) : (
        businesses.map((biz) => (
          <BusinessPanel
            key={biz.businessId}
            business={biz}
            plainKeys={plainKeys}
            onGenerate={handleGenerate}
            onRevoke={handleRevoke}
            onCopy={handleCopy}
            refreshing={refreshing}
          />
        ))
      )}
    </div>
  );
}

function BusinessPanel({
  business,
  plainKeys,
  onGenerate,
  onRevoke,
  onCopy,
  refreshing,
}) {
  const {
    businessId,
    businessName,
    keys = [],
    plan,
    keysUsed,
    keysLimit,
    storesLimit,
    storesUsed,
  } = business;

  const stores = Array.isArray(business.stores) ? business.stores : [];
  const storeIds = new Set(stores.map((store) => store.storeId));
  const unmatchedKeys = keys.filter((key) => !storeIds.has(key.storeId));
  const totalUsage = keys.reduce(
    (sum, key) => sum + (key.usageCount ?? 0),
    0
  );
  const monthlyUsage = keys.reduce(
    (sum, key) => sum + (key.monthlyRequestCount ?? 0),
    0
  );
  const usageAlerts = keys.filter((key) => usageStatusForKey(key, plan));
  const storeCount = stores.length;
  const storesLimitValue =
    typeof storesLimit === "number"
      ? storesLimit
      : typeof plan?.maxStores === "number"
      ? plan?.maxStores
      : null;
  const storesUsedValue =
    typeof storesUsed === "number" ? storesUsed : storeCount;
  const planMeta = resolvePlanMeta(plan);
  const planAccent = planMeta.accent;
  const monthlyLimit = plan?.secureRequestsPerMonth ?? null;
  const monthlyWindowLabel =
    keys.find((key) => key.usageWindowMonth)?.usageWindowMonth ?? null;
  const generationLockReason = resolveGenerationLock(plan, business);
  const canGenerateMore = business.canGenerateMore !== false;

  const planMetrics = [
    {
      label: "API keys",
      used: keysUsed ?? keys.length,
      limit:
        typeof keysLimit === "number"
          ? keysLimit
          : typeof plan?.maxKeys === "number"
          ? plan?.maxKeys
          : null,
      helper: "Active secure keys",
    },
    {
      label: "Stores",
      used: storesUsedValue,
      limit: storesLimitValue,
      helper: "Published menus connected",
    },
    {
      label: "Secure requests / month",
      used: monthlyUsage,
      limit: monthlyLimit,
      helper: monthlyWindowLabel
        ? `Current window ${monthlyWindowLabel}`
        : "Current calendar month",
    },
  ];

  return (
    <section className="integrations-business-card">
      <div className="integrations-business-header">
        <div>
          <h2>{businessName || "Your store"}</h2>
          <p className="integrations-subtitle">
            Secure embeds for{" "}
            {storeCount === 0
              ? "your published menus"
              : storeCount === 1
              ? "1 published menu"
              : `${storeCount} published menus`}
            .
          </p>
        </div>
        <div className="integrations-plan-card" style={{ borderColor: planAccent }}>
          <div
            className="plan-pill"
            style={{
              color: planAccent,
              background: planMeta.accentSoft,
            }}
          >
            {planMeta.name}
          </div>
          <p className="plan-card-description">{planMeta.description}</p>
        </div>
      </div>

      <div className="plan-metrics-grid">
        {planMetrics.map((metric) => (
          <PlanMetric
            key={metric.label}
            label={metric.label}
            used={metric.used}
            limit={metric.limit}
            helper={metric.helper}
            accent={planAccent}
          />
        ))}
        <PlanMetric
          label="Lifetime secure requests"
          used={totalUsage}
          helper="Combined usage across all time"
          accent={planAccent}
        />
      </div>

      {generationLockReason && (
        <div className="integrations-lockout">
          <AlertTriangle size={16} />
          <span>{generationLockReason}</span>
        </div>
      )}

      {usageAlerts.length > 0 && (
        <div className="integrations-warning integrations-usage-alert">
          <AlertTriangle size={16} />
          <span>
            We noticed a spike on{" "}
            {usageAlerts.length === 1
              ? "an API key"
              : `${usageAlerts.length} API keys`}
            . Review the embeds or rotate keys if this wasn’t expected.
          </span>
        </div>
      )}

      <div className="integrations-store-grid">
        {stores.length === 0 ? (
          <div className="integrations-empty">
            No published menus found yet. Publish a menu to generate a store
            ID.
          </div>
        ) : (
          stores.map((store) => (
            <StoreCard
              key={`${businessId}-${store.storeId}`}
              businessId={businessId}
              store={store}
              storeKeys={keys.filter((k) => k.storeId === store.storeId)}
              plainKeys={plainKeys}
              onGenerate={onGenerate}
              onRevoke={onRevoke}
              onCopy={onCopy}
              refreshing={refreshing}
              keysLimit={keysLimit}
              keysUsed={keysUsed}
              plan={plan}
              canGenerateMore={canGenerateMore}
              generationLockReason={generationLockReason}
            />
          ))
        )}
      </div>

      {unmatchedKeys.length > 0 && (
        <div className="integrations-unmatched">
          <h3>Keys for archived stores</h3>
          <p>
            These keys belong to stores that are no longer published. You can
            keep them for reference or revoke them if they are no longer in use.
          </p>
          <ul>
            {unmatchedKeys.map((key) => {
              const plaintext = plainKeys[key.keyId]?.key;
              return (
                <li key={key.keyId}>
                  <code>
                    {plaintext ? plaintext : key.maskedKey} ·{" "}
                    {key.storeId || "unknown store"}
                  </code>
                  {plaintext && (
                    <button
                      className="btn btn-text"
                      onClick={() =>
                        onCopy(
                          formatShortcode(key.storeId, plaintext),
                          "Shortcode copied to clipboard."
                        )
                      }
                    >
                      <Copy size={14} /> Copy shortcode
                    </button>
                  )}
                  {key.status === "active" && (
                    <button
                      className="btn btn-text danger"
                      disabled={refreshing}
                      onClick={() => onRevoke(businessId, key.keyId)}
                    >
                      Revoke
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function StoreCard({
  businessId,
  store,
  storeKeys,
  plainKeys,
  onGenerate,
  onRevoke,
  onCopy,
  refreshing,
  keysLimit,
  keysUsed,
  plan,
  canGenerateMore,
  generationLockReason,
}) {
  const displayName =
    store.storeName || store.storeSlug || store.storeId || "Store";
  const activeKeys = storeKeys.filter((key) => key.status === "active");
  const flaggedKeys = storeKeys.filter((key) => usageStatusForKey(key, plan));
  const allowGeneration = canGenerateMore !== false;
  const disableGenerate = refreshing || !allowGeneration;
  const lockMessage = !allowGeneration ? generationLockReason : null;

  return (
    <div className="integrations-store-card">
      <div className="integrations-store-card__header">
        <div>
          <h3>{displayName}</h3>
          <p>
            Store ID: <code>{store.storeId}</code>
          </p>
          <p className={`status status-${(store.status || "").toLowerCase()}`}>
            Status: {store.status || "unknown"}
          </p>
          <p className="integrations-store-card__meta">
            Active keys: {activeKeys.length}
          </p>
        </div>
        <button
          className="btn btn-primary"
          disabled={disableGenerate}
          onClick={() => onGenerate(businessId, store.storeId)}
        >
          <KeyRound size={16} />
          Generate key
        </button>
      </div>

      {lockMessage && (
        <div className="integrations-note">
          <AlertTriangle size={16} />
          <span>{lockMessage}</span>
        </div>
      )}

      {flaggedKeys.length > 0 && (
        <div className="integrations-note integrations-note-warning">
          <AlertTriangle size={16} />
          <span>
            {flaggedKeys.length === 1
              ? "One key is seeing unusually high traffic."
              : `${flaggedKeys.length} keys are seeing unusually high traffic.`}{" "}
            Consider investigating the embed configuration or rotating keys if
            this is unexpected.
          </span>
        </div>
      )}

      {storeKeys.length === 0 ? (
        <div className="integrations-empty">No keys have been generated.</div>
      ) : (
        <div className="integrations-key-table-wrapper">
          <table className="integrations-key-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Status</th>
                <th>Created</th>
                <th>This month</th>
                <th>Lifetime</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {storeKeys.map((key) => {
                const plaintext = plainKeys[key.keyId]?.key;
                const displayKey = plaintext ?? key.maskedKey;
                const shortcode = plaintext
                  ? formatShortcode(store.storeId, plaintext)
                  : null;
                const status = key.status || "unknown";
                const lifetimeUsage = key.usageCount ?? 0;
                const monthlyUsage = key.monthlyRequestCount ?? lifetimeUsage;
                const monthlyLimit = plan?.secureRequestsPerMonth ?? null;
                const usageStatus = usageStatusForKey(key, plan);
                const lastUsed = key.lastUsedAt
                  ? formatRelativeTime(key.lastUsedAt)
                  : null;
                const windowLabel = key.usageWindowMonth
                  ? `Window ${key.usageWindowMonth}`
                  : "Current month";
                const monthlyRatio =
                  typeof monthlyLimit === "number" && monthlyLimit > 0
                    ? Math.min(monthlyUsage / monthlyLimit, 1)
                    : null;
                const monthlyBarClass =
                  monthlyRatio === null
                    ? ""
                    : monthlyRatio >= 1
                    ? "is-critical"
                    : monthlyRatio >= 0.75
                    ? "is-warning"
                    : "";
                return (
                  <tr
                    key={key.keyId}
                    className={
                      usageStatus ? `usage-row usage-${usageStatus.level}` : ""
                    }
                  >
                    <td className="integrations-key-cell">
                      <code>{displayKey}</code>
                      {shortcode ? (
                        <button
                          className="btn btn-text"
                          onClick={() =>
                            onCopy(
                              shortcode,
                              "Shortcode copied to clipboard. Paste it into WordPress."
                            )
                          }
                        >
                          <Copy size={14} /> Copy shortcode
                        </button>
                      ) : (
                        <span className="integrations-muted">
                          Shortcode hidden for security. Generate a new key to
                          reveal it.
                        </span>
                      )}
                    </td>
                    <td>{status}</td>
                    <td>{formatDateTime(key.createdAt)}</td>
                    <td>
                      <div className="usage-metric">
                        <span className="usage-count">
                          {formatNumber(monthlyUsage)} request
                          {monthlyUsage === 1 ? "" : "s"}
                        </span>
                        {monthlyLimit && (
                          <>
                            <div className={`limit-bar ${monthlyBarClass}`}>
                              <div
                                className="limit-bar__fill"
                                style={{ width: `${monthlyRatio * 100}%` }}
                              />
                            </div>
                            <span className="limit-bar__caption">
                              {formatNumber(monthlyUsage)} /{" "}
                              {formatNumber(monthlyLimit)}
                            </span>
                          </>
                        )}
                        {usageStatus && (
                          <span
                            className={`usage-pill usage-pill-${usageStatus.level}`}
                            title={usageStatus.description}
                          >
                            {usageStatus.label}
                          </span>
                        )}
                        <span className="usage-window">{windowLabel}</span>
                      </div>
                    </td>
                    <td>
                      <div className="usage-metric">
                        <span className="usage-count">
                          {formatNumber(lifetimeUsage)} request
                          {lifetimeUsage === 1 ? "" : "s"}
                        </span>
                        {lastUsed && (
                          <span className="usage-window">
                            Last used {lastUsed}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="actions">
                      {status === "active" ? (
                        <button
                          className="btn btn-text danger"
                          disabled={refreshing}
                          onClick={() => onRevoke(businessId, key.keyId)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <span className="integrations-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanMetric({ label, used, limit, helper, accent }) {
  const hasLimit = typeof limit === "number" && limit > 0;
  const ratio = hasLimit ? Math.min(used / limit, 1) : null;
  const fillColor = accent || "var(--sys-primary-soft)";
  const barClass =
    ratio === null
      ? ""
      : ratio >= 1
      ? "is-critical"
      : ratio >= 0.75
      ? "is-warning"
      : "";

  return (
    <div className="metric-card">
      <div className="metric-card__label">{label}</div>
      <div className="metric-card__value">{formatLimitValue(used, limit)}</div>
      {helper && <div className="metric-card__helper">{helper}</div>}
      {ratio !== null && (
        <>
          <div className={`limit-bar ${barClass}`}>
            <div
              className="limit-bar__fill"
              style={{ width: `${ratio * 100}%`, background: fillColor }}
            />
          </div>
        </>
      )}
    </div>
  );
}

async function safeJsonMessage(response) {
  try {
    const data = await response.clone().json();
    return data?.message || data?.error;
  } catch {
    try {
      return await response.clone().text();
    } catch {
      return null;
    }
  }
}
